import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit, Trash } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const XeroLiteSettings: React.FC = () => {
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [accountFormOpen, setAccountFormOpen] = useState(false);
  const [editingTaxRate, setEditingTaxRate] = useState<any>(null);
  const [taxRateFormOpen, setTaxRateFormOpen] = useState(false);
  const [newAccount, setNewAccount] = useState({ code: '', name: '', type: 'Asset', account_category: 'Operating Expenses', is_active: true });
  const [newTaxRate, setNewTaxRate] = useState({ name: '', rate: 0 });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Load settings
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('settings')
        .select('*');
      
      if (error) throw error;
      
      // Convert to key-value object
      const settingsObj: Record<string, any> = {};
      data.forEach(setting => {
        try {
          settingsObj[setting.key] = JSON.parse(setting.value as string);
        } catch {
          settingsObj[setting.key] = setting.value;
        }
      });
      
      return settingsObj;
    }
  });

  // Load chart of accounts
  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['chart-of-accounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_of_accounts')
        .select('*')
        .order('code');
      
      if (error) throw error;
      return data;
    }
  });

  // Load tax rates (use default rates since tax_rates table may have different schema)
  const taxRates = [
    { id: '1', name: 'VAT 24%', rate: 24, is_active: true },
    { id: '2', name: 'VAT 13%', rate: 13, is_active: true },
    { id: '3', name: 'VAT 6%', rate: 6, is_active: true },
    { id: '4', name: 'VAT 0%', rate: 0, is_active: true }
  ];

  // Update setting mutation
  const updateSetting = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: any }) => {
      const { error } = await supabase
        .from('settings')
        .upsert({
          key,
          value: JSON.stringify(value)
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast({ title: "Setting updated successfully" });
    }
  });

  // Delete account
  const deleteAccount = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('chart_of_accounts')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
      toast({ title: "Account deleted successfully" });
    }
  });

  // Create new account
  const createAccount = useMutation({
    mutationFn: async (account: { code: string; name: string; type: string; account_category: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('chart_of_accounts')
        .insert([account]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
      setAccountFormOpen(false);
      setNewAccount({ code: '', name: '', type: 'Asset', account_category: 'Operating Expenses', is_active: true });
      toast({ title: "Account created successfully" });
    }
  });

  // Update account
  const updateAccount = useMutation({
    mutationFn: async ({ id, ...account }: { id: string; code: string; name: string; type: string; account_category: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('chart_of_accounts')
        .update(account)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chart-of-accounts'] });
      setAccountFormOpen(false);
      setEditingAccount(null);
      toast({ title: "Account updated successfully" });
    }
  });

  const handleSettingChange = (key: string, value: any) => {
    updateSetting.mutate({ key, value });
  };

  const handleCreateAccount = () => {
    if (newAccount.code && newAccount.name && newAccount.account_category) {
      createAccount.mutate(newAccount);
    }
  };

  const handleUpdateAccount = () => {
    if (editingAccount) {
      updateAccount.mutate(editingAccount);
    }
  };

  const accountTypes = ['Bank', 'Revenue', 'Direct Cost', 'Expense', 'Asset', 'Liability', 'Equity'];
  const accountCategories = ['Revenue', 'Direct Costs (excl. People)', 'Production Team Costs', 'Operating Expenses', 'Sales & Mgt Team Costs'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Settings</h1>
      </div>

      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle>General Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invoice_prefix">Invoice Prefix</Label>
            <Input
              id="invoice_prefix"
              value={settings?.invoice_prefix || 'INV'}
              onChange={(e) => handleSettingChange('invoice_prefix', e.target.value)}
              placeholder="INV"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bill_prefix">Bill Prefix</Label>
            <Input
              id="bill_prefix"
              value={settings?.bill_prefix || 'BILL'}
              onChange={(e) => handleSettingChange('bill_prefix', e.target.value)}
              placeholder="BILL"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="use_net_of_vat">Use Net of VAT by Default</Label>
              <p className="text-sm text-muted-foreground">
                Show amounts excluding VAT in reports
              </p>
            </div>
            <Switch
              id="use_net_of_vat"
              checked={settings?.use_net_of_vat || false}
              onCheckedChange={(checked) => handleSettingChange('use_net_of_vat', checked)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lock_date">Lock Date</Label>
            <Input
              id="lock_date"
              type="date"
              value={settings?.lock_date || ''}
              onChange={(e) => handleSettingChange('lock_date', e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Transactions before this date cannot be edited
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Chart of Accounts */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Chart of Accounts</CardTitle>
            <Button onClick={() => setAccountFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Account
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {accountsLoading ? (
            <div className="text-center py-8">Loading accounts...</div>
          ) : accounts && accounts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell className="font-mono">{account.code}</TableCell>
                    <TableCell className="font-medium">{account.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{account.type}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{account.account_category || 'Not Set'}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={!account.is_active ? 'secondary' : 'default'}>
                        {!account.is_active ? 'Inactive' : 'Active'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingAccount(account);
                            setAccountFormOpen(true);
                          }}
                          disabled={account.is_system}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this account?')) {
                              deleteAccount.mutate(account.id);
                            }
                          }}
                          disabled={account.is_system}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No chart of accounts found.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tax Rates */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Tax Rates</CardTitle>
            <Button onClick={() => setTaxRateFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Tax Rate
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {taxRates && taxRates.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taxRates.map((rate) => (
                  <TableRow key={rate.id}>
                    <TableCell className="font-medium">{rate.name}</TableCell>
                    <TableCell>{rate.rate}%</TableCell>
                    <TableCell>
                      <Badge variant={rate.is_active ? 'default' : 'secondary'}>
                        {rate.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setEditingTaxRate(rate);
                          setTaxRateFormOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No tax rates configured.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Account Form Dialog */}
      <Dialog open={accountFormOpen} onOpenChange={setAccountFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAccount ? 'Edit Account' : 'New Account'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="account-code">Account Code</Label>
              <Input
                id="account-code"
                value={editingAccount ? editingAccount.code : newAccount.code}
                onChange={(e) => {
                  if (editingAccount) {
                    setEditingAccount({ ...editingAccount, code: e.target.value });
                  } else {
                    setNewAccount({ ...newAccount, code: e.target.value });
                  }
                }}
                placeholder="e.g., 1000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account-name">Account Name</Label>
              <Input
                id="account-name"
                value={editingAccount ? editingAccount.name : newAccount.name}
                onChange={(e) => {
                  if (editingAccount) {
                    setEditingAccount({ ...editingAccount, name: e.target.value });
                  } else {
                    setNewAccount({ ...newAccount, name: e.target.value });
                  }
                }}
                placeholder="e.g., Cash at Bank"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account-type">Account Type</Label>
              <Select
                value={editingAccount ? editingAccount.type : newAccount.type}
                onValueChange={(value) => {
                  if (editingAccount) {
                    setEditingAccount({ ...editingAccount, type: value });
                  } else {
                    setNewAccount({ ...newAccount, type: value });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {accountTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="account-category">Account Category</Label>
              <Select
                value={editingAccount ? editingAccount.account_category : newAccount.account_category}
                onValueChange={(value) => {
                  if (editingAccount) {
                    setEditingAccount({ ...editingAccount, account_category: value });
                  } else {
                    setNewAccount({ ...newAccount, account_category: value });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-50 bg-background">
                  {accountCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="account-active">Active Status</Label>
                <p className="text-sm text-muted-foreground">
                  Whether this account is active and available for use
                </p>
              </div>
              <Switch
                id="account-active"
                checked={editingAccount ? editingAccount.is_active : newAccount.is_active}
                onCheckedChange={(checked) => {
                  if (editingAccount) {
                    setEditingAccount({ ...editingAccount, is_active: checked });
                  } else {
                    setNewAccount({ ...newAccount, is_active: checked });
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setAccountFormOpen(false);
                  setEditingAccount(null);
                  setNewAccount({ code: '', name: '', type: 'Asset', account_category: 'Operating Expenses', is_active: true });
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={editingAccount ? handleUpdateAccount : handleCreateAccount}
                disabled={createAccount.isPending || updateAccount.isPending}
              >
                {editingAccount ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tax Rate Form Dialog */}
      <Dialog open={taxRateFormOpen} onOpenChange={setTaxRateFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTaxRate ? 'Edit Tax Rate' : 'New Tax Rate'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tax-name">Tax Rate Name</Label>
              <Input
                id="tax-name"
                value={editingTaxRate ? editingTaxRate.name : newTaxRate.name}
                onChange={(e) => {
                  if (editingTaxRate) {
                    setEditingTaxRate({ ...editingTaxRate, name: e.target.value });
                  } else {
                    setNewTaxRate({ ...newTaxRate, name: e.target.value });
                  }
                }}
                placeholder="e.g., VAT 24%"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tax-rate">Rate (%)</Label>
              <Input
                id="tax-rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={editingTaxRate ? editingTaxRate.rate : newTaxRate.rate}
                onChange={(e) => {
                  const rate = parseFloat(e.target.value) || 0;
                  if (editingTaxRate) {
                    setEditingTaxRate({ ...editingTaxRate, rate });
                  } else {
                    setNewTaxRate({ ...newTaxRate, rate });
                  }
                }}
                placeholder="24"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setTaxRateFormOpen(false);
                  setEditingTaxRate(null);
                  setNewTaxRate({ name: '', rate: 0 });
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  // For now, just show a toast since tax rates are static
                  toast({ title: "Tax rate functionality coming soon" });
                  setTaxRateFormOpen(false);
                  setEditingTaxRate(null);
                  setNewTaxRate({ name: '', rate: 0 });
                }}
              >
                {editingTaxRate ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};